import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail } from "lucide-react";

interface EmailPreviewProps {
  firstName: string;
  lastName: string;
  planType?: string;
}

export const EmailPreview = ({ firstName, lastName, planType = "free" }: EmailPreviewProps) => {
  const [open, setOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const confirmationUrl = `${window.location.origin}/confirm-profile?token=beispiel-token-123`;
  const scanTime = new Date().toLocaleString("de-DE", {
    dateStyle: "full",
    timeStyle: "short",
  });

  const confirmationEmailHTML = `
    <!DOCTYPE html>
    <html lang="de">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f5f5;">
      <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 20px;">
        <tr>
          <td align="center">
            <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
              <tr>
                <td style="background: linear-gradient(135deg, #0072C6 0%, #29F49A 100%); padding: 30px 40px; text-align: center;">
                  <div style="background-color: white; display: inline-block; padding: 12px 24px; border-radius: 8px; margin-bottom: 10px;">
                    <h1 style="margin: 0; color: #0072C6; font-size: 32px; font-weight: bold; letter-spacing: -1px;">
                      MediQR
                    </h1>
                  </div>
                  <p style="margin: 0; color: white; font-size: 14px; font-weight: 500;">Dein digitaler Notfallausweis</p>
                </td>
              </tr>
              <tr>
                <td style="padding: 40px;">
                  <h2 style="color: #1F2937; font-size: 24px; margin: 0 0 20px 0; font-weight: 600;">
                    Hallo ${firstName} ${lastName}!
                  </h2>
                  <p style="color: #4B5563; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                    Bitte bestätige dein MediQR Profil, um es weiterhin nutzen zu können.
                  </p>
                  <p style="color: #4B5563; font-size: 16px; line-height: 1.6; margin: 0 0 30px 0;">
                    Klicke auf den folgenden Button, um dein Profil zu bestätigen:
                  </p>
                  <table width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td align="center" style="padding: 20px 0;">
                        <a href="${confirmationUrl}" style="background: linear-gradient(135deg, #0072C6 0%, #29F49A 100%); color: white; padding: 16px 40px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600; font-size: 16px; box-shadow: 0 4px 12px rgba(0, 114, 198, 0.4);">
                          Profil bestätigen
                        </a>
                      </td>
                    </tr>
                  </table>
                  <p style="color: #6B7280; font-size: 14px; line-height: 1.6; margin: 30px 0 10px 0;">
                    Oder kopiere diesen Link in deinen Browser:
                  </p>
                  <div style="background-color: #F3F4F6; padding: 12px; border-radius: 6px; word-break: break-all;">
                    <a href="${confirmationUrl}" style="color: #0072C6; font-size: 13px; text-decoration: none;">
                      ${confirmationUrl}
                    </a>
                  </div>
                  <div style="background-color: #FEF3C7; border-left: 4px solid #F59E0B; padding: 16px; margin: 30px 0; border-radius: 4px;">
                    <p style="margin: 0; color: #92400E; font-size: 14px; line-height: 1.6;">
                      <strong>⏰ Wichtig:</strong> Dieser Link ist 48 Stunden gültig.
                    </p>
                  </div>
                </td>
              </tr>
              <tr>
                <td style="background-color: #F9FAFB; padding: 30px 40px; border-top: 1px solid #E5E7EB;">
                  <p style="color: #6B7280; font-size: 14px; line-height: 1.6; margin: 0 0 15px 0;">
                    Beste Grüße,<br>
                    <strong style="color: #0072C6;">Dein MediQR Team</strong>
                  </p>
                  <p style="color: #9CA3AF; font-size: 12px; line-height: 1.6; margin: 20px 0 0 0;">
                    Diese E-Mail wurde automatisch versendet. Bitte antworte nicht auf diese E-Mail.
                  </p>
                  <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #E5E7EB;">
                    <p style="color: #9CA3AF; font-size: 11px; line-height: 1.5; margin: 0;">
                      © ${currentYear} MediQR. Alle Rechte vorbehalten.<br>
                      <a href="https://mediqr.de" style="color: #0072C6; text-decoration: none;">mediqr.de</a>
                    </p>
                  </div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;

  const reminderEmailHTML = `
    <!DOCTYPE html>
    <html lang="de">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f5f5;">
      <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 20px;">
        <tr>
          <td align="center">
            <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
              <tr>
                <td style="background: linear-gradient(135deg, #0072C6 0%, #29F49A 100%); padding: 30px 40px; text-align: center;">
                  <div style="background-color: white; display: inline-block; padding: 12px 24px; border-radius: 8px; margin-bottom: 10px;">
                    <h1 style="margin: 0; color: #0072C6; font-size: 32px; font-weight: bold; letter-spacing: -1px;">
                      MediQR
                    </h1>
                  </div>
                  <p style="margin: 0; color: white; font-size: 14px; font-weight: 500;">Dein digitaler Notfallausweis</p>
                </td>
              </tr>
              <tr>
                <td style="background-color: #F59E0B; padding: 20px 40px; text-align: center;">
                  <p style="margin: 0; color: white; font-size: 18px; font-weight: 600;">
                    ⏰ Dein Profil läuft in 5 Tag(en) ab.
                  </p>
                </td>
              </tr>
              <tr>
                <td style="padding: 40px;">
                  <h2 style="color: #1F2937; font-size: 24px; margin: 0 0 20px 0; font-weight: 600;">
                    Hallo ${firstName} ${lastName}!
                  </h2>
                  <p style="color: #4B5563; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                    Bitte bestätige dein MediQR Profil, um es weiterhin nutzen zu können.
                  </p>
                  <div style="background-color: #F3F4F6; padding: 20px; border-radius: 8px; margin: 20px 0;">
                    <p style="margin: 0; color: #374151; font-size: 15px; line-height: 1.6;">
                      📋 <strong>Dein Plan:</strong> ${planType === "premium" ? "Premium" : "Basis"}<br>
                      🔄 <strong>Bestätigung erforderlich:</strong> Alle ${planType === "premium" ? "180" : "30"} Tage
                    </p>
                  </div>
                  <table width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td align="center" style="padding: 20px 0;">
                        <a href="${confirmationUrl}" style="background: linear-gradient(135deg, #0072C6 0%, #29F49A 100%); color: white; padding: 16px 40px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600; font-size: 16px; box-shadow: 0 4px 12px rgba(0, 114, 198, 0.4);">
                          Profil jetzt bestätigen
                        </a>
                      </td>
                    </tr>
                  </table>
                  ${planType === "free" ? `
                  <div style="background-color: #DBEAFE; border-left: 4px solid #3B82F6; padding: 16px; margin: 20px 0; border-radius: 4px;">
                    <p style="margin: 0; color: #1E40AF; font-size: 14px; line-height: 1.6;">
                      <strong>💡 Tipp:</strong> Mit einem Premium-Plan musst du dein Profil nur alle 180 Tage bestätigen, mit Pro gar nicht mehr!
                    </p>
                  </div>
                  ` : ''}
                </td>
              </tr>
              <tr>
                <td style="background-color: #F9FAFB; padding: 30px 40px; border-top: 1px solid #E5E7EB;">
                  <p style="color: #6B7280; font-size: 14px; line-height: 1.6; margin: 0 0 15px 0;">
                    Beste Grüße,<br>
                    <strong style="color: #0072C6;">Dein MediQR Team</strong>
                  </p>
                  <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #E5E7EB;">
                    <p style="color: #9CA3AF; font-size: 11px; line-height: 1.5; margin: 0;">
                      © ${currentYear} MediQR. Alle Rechte vorbehalten.<br>
                      <a href="https://mediqr.de" style="color: #0072C6; text-decoration: none;">mediqr.de</a>
                    </p>
                  </div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;

  const scanNotificationEmailHTML = `
    <!DOCTYPE html>
    <html lang="de">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f5f5;">
      <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 20px;">
        <tr>
          <td align="center">
            <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
              <tr>
                <td style="background: linear-gradient(135deg, #0072C6 0%, #29F49A 100%); padding: 30px 40px; text-align: center;">
                  <div style="background-color: white; display: inline-block; padding: 12px 24px; border-radius: 8px; margin-bottom: 10px;">
                    <h1 style="margin: 0; color: #0072C6; font-size: 32px; font-weight: bold; letter-spacing: -1px;">
                      MediQR
                    </h1>
                  </div>
                  <p style="margin: 0; color: white; font-size: 14px; font-weight: 500;">Dein digitaler Notfallausweis</p>
                </td>
              </tr>
              <tr>
                <td style="background-color: #FEE2E2; padding: 20px 40px; text-align: center; border-bottom: 3px solid #DC2626;">
                  <p style="margin: 0; color: #991B1B; font-size: 18px; font-weight: 600;">
                    🚨 Notfallprofil-Scan erkannt
                  </p>
                </td>
              </tr>
              <tr>
                <td style="padding: 40px;">
                  <h2 style="color: #1F2937; font-size: 24px; margin: 0 0 20px 0; font-weight: 600;">
                    Hallo!
                  </h2>
                          <p style="color: #4B5563; font-size: 16px; line-height: 1.6; margin: 0 0 30px 0;">
                            Ihr Notfallprofil für <strong style="color: #0072C6;">${firstName} ${lastName}</strong> wurde gerade gescannt.
                          </p>
                  <div style="background-color: #F9FAFB; border: 2px solid #E5E7EB; padding: 24px; border-radius: 8px; margin: 30px 0;">
                    <h3 style="color: #374151; font-size: 16px; margin: 0 0 16px 0; font-weight: 600;">
                      📊 Scan-Details
                    </h3>
                    <table width="100%" cellpadding="8" cellspacing="0">
                      <tr>
                        <td style="color: #6B7280; font-size: 14px; padding: 8px 0; border-bottom: 1px solid #E5E7EB;">
                          <strong>⏰ Zeitpunkt:</strong>
                        </td>
                        <td style="color: #1F2937; font-size: 14px; padding: 8px 0; border-bottom: 1px solid #E5E7EB; text-align: right;">
                          ${scanTime}
                        </td>
                      </tr>
                      <tr>
                        <td style="color: #6B7280; font-size: 14px; padding: 8px 0; border-bottom: 1px solid #E5E7EB;">
                          <strong>📍 Standort:</strong>
                        </td>
                        <td style="color: #1F2937; font-size: 14px; padding: 8px 0; border-bottom: 1px solid #E5E7EB; text-align: right;">
                          Berlin, Deutschland
                        </td>
                      </tr>
                      <tr>
                        <td style="color: #6B7280; font-size: 14px; padding: 8px 0;">
                          <strong>💻 Gerät:</strong>
                        </td>
                        <td style="color: #1F2937; font-size: 14px; padding: 8px 0; text-align: right;">
                          iPhone, Safari
                        </td>
                      </tr>
                    </table>
                  </div>
                  <div style="background-color: #FEF3C7; border-left: 4px solid #F59E0B; padding: 16px; margin: 30px 0; border-radius: 4px;">
                    <p style="margin: 0; color: #92400E; font-size: 14px; line-height: 1.6;">
                      <strong>ℹ️ Hinweis:</strong> Dies kann bedeuten, dass jemand Ihren QR-Code in einer Notfallsituation gescannt hat.
                    </p>
                  </div>
                  <table width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td align="center" style="padding: 20px 0;">
                        <a href="${window.location.origin}/dashboard" style="background: linear-gradient(135deg, #0072C6 0%, #29F49A 100%); color: white; padding: 16px 40px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600; font-size: 16px; box-shadow: 0 4px 12px rgba(0, 114, 198, 0.4);">
                          📊 Zur Statistik-Seite
                        </a>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="background-color: #F9FAFB; padding: 30px 40px; border-top: 1px solid #E5E7EB;">
                  <p style="color: #6B7280; font-size: 14px; line-height: 1.6; margin: 0 0 15px 0;">
                    Beste Grüße,<br>
                    <strong style="color: #0072C6;">Dein MediQR Team</strong>
                  </p>
                  <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #E5E7EB;">
                    <p style="color: #9CA3AF; font-size: 11px; line-height: 1.5; margin: 0;">
                      © ${currentYear} MediQR. Alle Rechte vorbehalten.<br>
                      <a href="https://mediqr.de" style="color: #0072C6; text-decoration: none;">mediqr.de</a>
                    </p>
                  </div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Mail className="mr-2 h-4 w-4" />
          E-Mail-Vorschau
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>E-Mail-Template Vorschau</DialogTitle>
          <DialogDescription>
            Vorschau aller E-Mail-Benachrichtigungen, die an Nutzer versendet werden
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="confirmation" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="confirmation">Bestätigung</TabsTrigger>
            <TabsTrigger value="reminder">Erinnerung</TabsTrigger>
            <TabsTrigger value="scan">Scan-Benachrichtigung</TabsTrigger>
          </TabsList>
          
          <TabsContent value="confirmation" className="flex-1 overflow-auto mt-4">
            <div className="border rounded-lg overflow-hidden">
              <iframe
                srcDoc={confirmationEmailHTML}
                title="Confirmation Email Preview"
                className="w-full h-[600px] border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="reminder" className="flex-1 overflow-auto mt-4">
            <div className="border rounded-lg overflow-hidden">
              <iframe
                srcDoc={reminderEmailHTML}
                title="Reminder Email Preview"
                className="w-full h-[600px] border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="scan" className="flex-1 overflow-auto mt-4">
            <div className="border rounded-lg overflow-hidden">
              <iframe
                srcDoc={scanNotificationEmailHTML}
                title="Scan Notification Email Preview"
                className="w-full h-[600px] border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
